import { useState } from "react";
import { UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Blocking onboarding card for client accounts. Rendered by ClientLayout
 * instead of the page content until full name, company AND phone are all
 * filled in — a new signup can't progress without completing it.
 */
export function ClientProfileGate() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    phone: user?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company.trim() || !form.phone.trim()) {
      setError("Please fill in all fields to continue.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.patch("/client/profile", {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
      });
      await refresh();
      toast.success("Profile complete — welcome aboard!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const field = (id, label, type = "text", autoComplete) => (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C]">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        autoComplete={autoComplete}
        required
        value={form[id]}
        onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
        className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
        data-testid={`profile-gate-${id}-input`}
      />
    </div>
  );

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10" data-testid="client-profile-gate">
      <div className="w-full max-w-md rounded-2xl border border-[#0088CC]/25 bg-[#F6F7F8] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#DDE5EC] bg-gradient-to-r from-[#E6F2FA] to-transparent flex items-center gap-2">
          <UserRound className="h-4 w-4 text-[#0088CC]" />
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-[#011836]">
            Complete your profile
          </h2>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <p className="text-sm text-[#5B6B7C]">
            Before you dive in, tell us a little about yourself so our team
            knows who they&apos;re talking to.
          </p>
          {field("name", "Full name", "text", "name")}
          {field("company", "Company", "text", "organization")}
          {field("phone", "Phone", "tel", "tel")}
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C]">Email</Label>
            <div className="text-sm text-[#011836] px-3 py-2 rounded-md border border-[#DDE5EC] bg-white/60" data-testid="profile-gate-email">
              {user?.email}
            </div>
          </div>
          {error && (
            <div className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded" data-testid="profile-gate-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide"
            data-testid="profile-gate-submit-btn"
          >
            {saving ? "Saving…" : "Save & continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
